/**
 * HR 휴가 현황 페이지
 *
 * 전체 직원의 휴가 잔액 및 사용 현황을 조회하고 관리하는 페이지
 *
 * V197: 엑셀 일괄 임포트 기능 추가
 * V189: 월별 사용량을 leaveHistories SSoT 기반으로 조회
 *       - leaveBalances.annual.monthlyUsage 캐시 대신 실시간 이력 집계
 *       - source='import' 이력은 월별 분해 불가 → 총 사용에만 반영
 */

import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Calendar, Download, Filter, Upload, X, AlertCircle, CheckCircle, RefreshCw, Square, Edit2, UserMinus } from 'lucide-react';
import { useAllLeaveBalances } from '../../shared/hooks/useLeaveBalances';
// V64 최적화: useAllLeaveRequests 제거 (전체 leaveRequests 스캔 → pendingRequests는 UI 미사용)
import { useEmployees } from '../../shared/hooks/useEmployees';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { useLeaveMonthlyUsageBatch, PeriodInfo } from '../../shared/hooks/useLeaveMonthlyUsage';
import { EMPLOYEE_STATUS_LABELS, EMPLOYEE_STATUS_COLORS } from '../../shared/constants/employeeStatus';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import * as XLSX from 'xlsx';
import { LeaveBalanceEditModal } from './components/LeaveBalanceEditModal';
import { EmployeeLeaveUsageDetailModal } from './components/EmployeeLeaveUsageDetailModal';
import { LeaveBalance } from '../../shared/types/leaveBalance';
import { ModalOverlay } from '../../shared/components/ModalOverlay';

// V36.2: syncAllLeaveBalances 입력 타입
interface SyncAllLeaveBalancesInput {
  companyCode?: 'KR' | 'CN' | null;
  cursor?: string | null;
  limit?: number | null;
}

// V36.2: syncAllLeaveBalances 응답 타입
interface SyncAllLeaveBalancesResponse {
  success: boolean;
  processedCount: number;
  syncedCount: number;
  errorCount: number;
  errors: Array<{ employeeId: string; error: string }>;
  companyCode: string;
  nextCursor: string | null;
  executedAt: string;
}

// V36.2: 동기화 진행 상태
type SyncStatus = 'idle' | 'running' | 'completed' | 'aborted' | 'failed';

// V36.2: 동기화 진행 상황
interface SyncProgress {
  status: SyncStatus;
  companyCode: string;
  processed: number;
  synced: number;
  errors: number;
  message: string;
}

// V197: 엑셀 컬럼 매핑 (기본)
const LEAVE_EXCEL_COLUMN_MAPPING: Record<string, string> = {
  '사번': 'employeeNumber',
  '이름': 'employeeName',
  '조직': 'orgUnitName',
  '부서': 'orgUnitName',
  '발생일': 'accrualDate',
  '적용시작일': 'validFrom',
  '적용종료일': 'validUntil',
  '발생': 'daysGranted',
  '발생일수': 'daysGranted',
  '사용일': 'daysUsed',
  '사용': 'daysUsed',
  '잔여일': 'daysRemaining',
  '잔여': 'daysRemaining',
  // V39.x: 선사용 연차만 지원 (previousYearUsed 레거시 제거)
  '선사용연차': 'preUsed',
  '선사용': 'preUsed',
  // V47.5: 전년도사용일 (해를 걸치는 Period용)
  '전년도사용일': 'prevYearUsed',
  '전년도사용': 'prevYearUsed',
  '1월': 'month_1',
  '2월': 'month_2',
  '3월': 'month_3',
  '4월': 'month_4',
  '5월': 'month_5',
  '6월': 'month_6',
  '7월': 'month_7',
  '8월': 'month_8',
  '9월': 'month_9',
  '10월': 'month_10',
  '11월': 'month_11',
  '12월': 'month_12',
  '퍼스널1분기': 'personal_1',
  '퍼스널2분기': 'personal_2',
  '퍼스널3분기': 'personal_3',
  '퍼스널4분기': 'personal_4',
  // V45: 퍼힐1Q~4Q 헤더 추가 (엑셀 내보내기 헤더와 일치)
  '퍼힐1Q': 'personal_1',
  '퍼힐2Q': 'personal_2',
  '퍼힐3Q': 'personal_3',
  '퍼힐4Q': 'personal_4',
  // V65: 퍼스널힐링데이 full prefix 변형
  '퍼스널힐링데이1분기': 'personal_1',
  '퍼스널힐링데이2분기': 'personal_2',
  '퍼스널힐링데이3분기': 'personal_3',
  '퍼스널힐링데이4분기': 'personal_4',
  '퍼스널힐링1분기': 'personal_1',
  '퍼스널힐링2분기': 'personal_2',
  '퍼스널힐링3분기': 'personal_3',
  '퍼스널힐링4분기': 'personal_4',
};

// V199: 헤더 alias 맵 (공백/변형 대응)
const HEADER_ALIAS_MAP: Record<string, string> = {
  // 사번 변형
  '사 번': '사번',
  '직원번호': '사번',
  '사원번호': '사번',
  'employeenumber': '사번',
  'emp_no': '사번',
  'empno': '사번',
  // 이름 변형
  '성명': '이름',
  '직원명': '이름',
  '사원명': '이름',
  'name': '이름',
  // 조직 변형
  '소속': '조직',
  '팀': '조직',
  '부서명': '조직',
  '조직명': '조직',
  // 기타
  '발생 일수': '발생일수',
  '사용 일수': '사용일',
  '잔여 일수': '잔여일',
  // V39.x: 선사용 연차만 지원 (previousYearUsed alias 제거)
  '선사용 연차': '선사용연차',
  // V47.5: 전년도사용일 alias
  '전년도 사용일': '전년도사용일',
  '전년도 사용': '전년도사용',
  // V45.1: 퍼힐 헤더 변형 (공백 포함)
  '퍼힐 1Q': '퍼힐1Q',
  '퍼힐 2Q': '퍼힐2Q',
  '퍼힐 3Q': '퍼힐3Q',
  '퍼힐 4Q': '퍼힐4Q',
  '퍼스널 1분기': '퍼스널1분기',
  '퍼스널 2분기': '퍼스널2분기',
  '퍼스널 3분기': '퍼스널3분기',
  '퍼스널 4분기': '퍼스널4분기',
  // V65: 퍼스널힐링데이 full prefix alias (공백 포함)
  '퍼스널힐링데이 1분기': '퍼스널힐링데이1분기',
  '퍼스널힐링데이 2분기': '퍼스널힐링데이2분기',
  '퍼스널힐링데이 3분기': '퍼스널힐링데이3분기',
  '퍼스널힐링데이 4분기': '퍼스널힐링데이4분기',
  '퍼스널힐링 1분기': '퍼스널힐링1분기',
  '퍼스널힐링 2분기': '퍼스널힐링2분기',
  '퍼스널힐링 3분기': '퍼스널힐링3분기',
  '퍼스널힐링 4분기': '퍼스널힐링4분기',
};

// V199: 필수 헤더 목록
const REQUIRED_HEADERS = ['사번'];
const RECOMMENDED_HEADERS = ['이름', '발생', '사용', '잔여'];

/**
 * V199: 헤더 정규화 함수
 * - 공백/개행/탭 제거
 * - alias 매핑 적용
 */
function normalizeHeader(header: any): string {
  if (header === null || header === undefined) return '';
  // 문자열 변환 + 공백/개행/탭 모두 제거
  const normalized = String(header).replace(/[\s\r\n\t]/g, '').trim();
  // alias 체크 (대소문자 무시)
  const lowerNormalized = normalized.toLowerCase();
  for (const [alias, canonical] of Object.entries(HEADER_ALIAS_MAP)) {
    if (alias.toLowerCase() === lowerNormalized) {
      return canonical;
    }
  }
  return normalized;
}

/**
 * V45.1: 퍼힐 값 파싱 함수
 * - "사용/미사용" 텍스트 지원 (운영 정책)
 * - 숫자 입력도 하위 호환 지원
 *
 * @returns 사용이면 1, 미사용이면 null (CF에서 quarterlyUsage[q] > 0 => qUsed=true)
 */
function parsePersonalHealingValue(value: any): number | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  // 문자열로 변환 후 공백 제거 + 소문자화
  const strValue = String(value).replace(/\s/g, '').toLowerCase();

  // "사용"으로 간주되는 값들
  const USED_VALUES = ['사용', 'y', 'yes', 'true', 'o', '○', '●', 'used', '1'];
  if (USED_VALUES.includes(strValue)) {
    return 1; // CF에서 > 0 체크하므로 1 반환
  }

  // "미사용"으로 간주되는 값들
  const UNUSED_VALUES = ['미사용', 'n', 'no', 'false', 'x', '×', 'unused', '0', '-'];
  if (UNUSED_VALUES.includes(strValue)) {
    return null; // 미사용 = 업데이트 안함
  }

  // 숫자인 경우 (하위 호환: 0.5, 1 등)
  const parsed = parseFloat(strValue);
  if (!isNaN(parsed)) {
    return parsed > 0 ? parsed : null;
  }

  // 인식 불가 → 미사용 처리
  return null;
}

/**
 * V199: 헤더 행 점수 계산
 * - 필수/권장 헤더 매칭 개수로 점수 산정
 */
function calculateHeaderScore(row: any[]): { score: number; matchedHeaders: string[] } {
  const allKnownHeaders = Object.keys(LEAVE_EXCEL_COLUMN_MAPPING);
  const matchedHeaders: string[] = [];

  for (const cell of row) {
    const normalized = normalizeHeader(cell);
    if (allKnownHeaders.includes(normalized)) {
      matchedHeaders.push(normalized);
    }
  }

  // 필수 헤더 보너스
  const hasRequired = REQUIRED_HEADERS.every(req => matchedHeaders.includes(req));
  const recommendedCount = RECOMMENDED_HEADERS.filter(rec => matchedHeaders.includes(rec)).length;

  const score = matchedHeaders.length + (hasRequired ? 10 : 0) + recommendedCount;
  return { score, matchedHeaders };
}

/**
 * V199: 데이터가 있는 시트 찾기
 * - 각 시트의 상위 10행을 스캔하여 헤더 점수가 가장 높은 시트 반환
 */
function findBestSheet(workbook: XLSX.WorkBook): { sheetName: string; headerRowIndex: number; headers: string[] } | null {
  let bestResult: { sheetName: string; headerRowIndex: number; score: number; headers: string[] } | null = null;

  for (const sheetName of workbook.SheetNames) {
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

    // 상위 10행 스캔
    const scanLimit = Math.min(10, jsonData.length);
    for (let rowIdx = 0; rowIdx < scanLimit; rowIdx++) {
      const row = jsonData[rowIdx];
      if (!row || row.length === 0) continue;

      const { score, matchedHeaders } = calculateHeaderScore(row);

      // 최소 필수 헤더(사번) 포함 + 2개 이상 매칭
      if (score > 0 && matchedHeaders.length >= 2 && matchedHeaders.includes('사번')) {
        if (!bestResult || score > bestResult.score) {
          bestResult = {
            sheetName,
            headerRowIndex: rowIdx,
            score,
            headers: matchedHeaders,
          };
        }
      }
    }
  }

  return bestResult;
}

// V199: 파싱 실패 상세 타입
interface ParseError {
  type: 'EMPTY_SHEET' | 'HEADER_NOT_FOUND' | 'MISSING_REQUIRED' | 'NO_DATA_ROWS';
  message: string;
  details?: {
    foundHeaders?: string[];
    missingHeaders?: string[];
    scannedSheets?: string[];
  };
}

// V197: 임포트 결과 타입
interface ImportRowResult {
  rowIndex: number;
  employeeNumber: string;
  employeeName?: string;
  success: boolean;
  error?: string;
}

// V47.14: 기간 불일치 항목
interface PeriodMismatchItem {
  employeeId: string;
  employeeNumber: string;
  employeeName?: string;
  excelValidFrom?: string;
  excelValidUntil?: string;
  systemValidFrom?: string;
  systemValidUntil?: string;
  reason: 'MISMATCH' | 'EXCEL_MISSING' | 'SYSTEM_MISSING' | 'COMPUTE_ERROR';
}

interface ImportResult {
  success: boolean;
  totalCount: number;
  successCount: number;
  failureCount: number;
  results: ImportRowResult[];
  // V47.14: 기간 불일치 정보
  mismatchPeriods?: PeriodMismatchItem[];
  mismatchCount?: number;
  // V64: 발생일수 정책 불일치 정보
  grantedMismatches?: GrantedMismatchItem[];
  grantedMismatchCount?: number;
}

// V64: 발생일수 정책 불일치 항목
interface GrantedMismatchItem {
  employeeId: string;
  employeeNumber: string;
  employeeName?: string;
  periodType: string;
  excelGranted: number;
  policyGranted: number;
  reason: string;
}

/**
 * V70.4: 휴가현황 표시값 산출 (테이블 행 = 엑셀 행 SSoT)
 *
 * 화면 테이블과 엑셀 다운로드가 **반드시 동일한 값**을 쓰도록, 표시값 산출을 이 함수 하나로 모은다.
 * 엑셀용 계산식을 따로 만들지 않는다 — 아래 로직은 기존 테이블 렌더 코드를 그대로 옮긴 것이며
 * 신규 계산은 없다.
 *
 * - 월별 사용량: V65-FIX 정책 그대로 — leaveBalances.annual.monthlyUsage(DB) 우선,
 *   없으면 useLeaveMonthlyUsageBatch(leaveHistories) 폴백.
 * - 적용시작/종료: CN INTERN 은 '-' (기존 화면 분기 유지)
 * - balance 문서가 없으면 수치 컬럼은 '-' (기존 화면 표기 유지)
 */
function deriveLeaveOverviewRow(
  item: { employee?: any; balance?: any },
  monthlyUsageMap: Record<string, { monthlyUsage?: Record<number, number> } | undefined>
) {
  const employee = item.employee;
  const balance = item.balance;
  const isCnIntern =
    employee?.companyCode === 'CN' && employee?.currentEmploymentTypeCode === 'INTERN';

  // 월별 사용량 (balance 우선 → histories 폴백)
  const employeeId = balance?.employeeId;
  const usageFromHistories = employeeId ? monthlyUsageMap[employeeId]?.monthlyUsage : undefined;
  const balanceMonthlyUsage = (balance?.annual?.monthlyUsage || {}) as Record<number, number>;
  const monthlyUsage: Record<number, number> = {};
  for (let m = 1; m <= 12; m++) {
    if (balanceMonthlyUsage[m] !== undefined) {
      monthlyUsage[m] = balanceMonthlyUsage[m];
    } else if (usageFromHistories && usageFromHistories[m] !== undefined) {
      monthlyUsage[m] = usageFromHistories[m];
    }
  }

  return {
    companyCode: (employee?.companyCode as string) ?? '',
    employeeNumber: employee?.employeeNumber ?? '-',
    nameKo: employee?.nameKo ?? balance?.employeeName ?? '-',
    orgUnitName: employee?.orgUnitName ?? '-',
    granted: balance ? (balance.annual?.granted ?? 0) : '-',
    used: balance ? (balance.annual?.used ?? 0) : '-',
    preUsed: balance ? (balance.annual?.preUsed ?? 0) : '-',
    remaining: balance ? (balance.annual?.remaining ?? 0) : '-',
    validFrom: isCnIntern ? '-' : (balance?.annual?.validFrom ?? balance?.annual?.periodStart ?? '-'),
    validUntil: isCnIntern ? '-' : (balance?.annual?.validUntil ?? balance?.annual?.periodEnd ?? '-'),
    monthlyUsage,
    q1Used: !!balance?.personalHealing?.q1Used,
    q2Used: !!balance?.personalHealing?.q2Used,
    q3Used: !!balance?.personalHealing?.q3Used,
    q4Used: !!balance?.personalHealing?.q4Used,
  };
}

export function HrLeaveOverviewPage() {
  const navigate = useNavigate(); // V67: 휴직 현황 보기 버튼
  const { balances, isLoading: balancesLoading, refetch } = useAllLeaveBalances();
  // V64 최적화: useAllLeaveRequests 제거 (전체 컬렉션 스캔, pendingRequests UI 미사용)
  // V36.2.5: status='active_or_on_leave'로 재직/휴직자만 조회 (퇴사자 제외)
  const { employees, isLoading: employeesLoading } = useEmployees({ status: 'active_or_on_leave' });

  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  // V311: 월별 사용량 강제 재조회 키
  const [monthlyRefreshKey, setMonthlyRefreshKey] = useState(0);

  // V197: 임포트 관련 상태
  const [showImportModal, setShowImportModal] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [targetYear, setTargetYear] = useState(new Date().getFullYear());
  const [includePreviousYear, setIncludePreviousYear] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // V43: 수동 수정 모달 관련 상태
  const [showEditModal, setShowEditModal] = useState(false);
  // V70.4: 엑셀 다운로드 중 중복 클릭 방지 (기존 엑셀 다운로드 페이지들과 동일 패턴)
  const [isExporting, setIsExporting] = useState(false);
  const [editTargetEmployee, setEditTargetEmployee] = useState<{
    employeeId: string;
    employeeName: string;
    employeeNumber?: string;
    balance: LeaveBalance | null;
  } | null>(null);

  // V82: 상세 휴가 사용 내역 모달 상태
  const [usageDetailTarget, setUsageDetailTarget] = useState<{
    employeeId: string;
    employeeName: string;
    employeeNumber?: string;
  } | null>(null);

  // V36.2: 일괄 Sync 관련 상태 (커서 기반)
  const [syncProgress, setSyncProgress] = useState<SyncProgress>({
    status: 'idle',
    companyCode: '',
    processed: 0,
    synced: 0,
    errors: 0,
    message: '',
  });
  const [selectedCompanyCode, setSelectedCompanyCode] = useState<'KR' | 'CN'>('KR');
  const abortControllerRef = useRef<AbortController | null>(null);

  // V36.2: 현재 사용자 정보 (companyCode 판정용)
  const { user: currentUser, isLoading: userLoading } = useCurrentUser();

  // V36.2.1 Hotfix: 사용자 역할에 따른 회사 선택 가능 여부
  const userCompanyCode = currentUser?.profile?.companyCode as 'KR' | 'CN' | undefined;
  const userRoles = currentUser?.roles || [];
  const isGlobalAdmin = userRoles.some(role =>
    ['system_admin', 'role_global'].includes(role)
  );

  // V60: role_exec 읽기 전용 처리
  const { hasPermission } = useAuthorization();
  const canManageHr = hasPermission('hr.manage');

  // V36.2.1 Hotfix: 전역 관리자는 회사 선택 가능, 그 외는 본인 회사만
  // userLoading 중이거나 userCompanyCode 미확정 시 빈 배열 (버튼 비활성화)
  const availableCompanyCodes: Array<'KR' | 'CN'> = userLoading
    ? []
    : isGlobalAdmin
      ? ['KR', 'CN']
      : (userCompanyCode ? [userCompanyCode] : []);

  // V36.2.1 Hotfix: 동기화 버튼 비활성화 사유 계산
  const syncButtonDisabled = userLoading || availableCompanyCodes.length === 0;
  const syncButtonDisabledReason = userLoading
    ? '사용자 정보 확인 중...'
    : availableCompanyCodes.length === 0
      ? '회사 코드를 판정할 수 없습니다'
      : '';

  /**
   * V199: 엑셀 파싱 함수 (안전성 강화)
   *
   * 개선사항:
   * 1. 데이터가 있는 시트 자동 탐색 (첫 시트가 안내 시트여도 통과)
   * 2. 헤더 정규화 (trim, 공백/개행 제거) + alias map 적용
   * 3. 헤더 행 자동 탐지 (상위 10행 스캔)
   * 4. 상세 에러 메시지 (시트 비었음/헤더 불일치/필수 컬럼 누락)
   */
  const parseExcelFile = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });

          // V199: 데이터가 있는 시트 자동 탐색
          const bestSheet = findBestSheet(workbook);

          if (!bestSheet) {
            // 모든 시트를 스캔했지만 유효한 헤더를 찾지 못함
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const firstSheetData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];

            if (firstSheetData.length === 0) {
              const error: ParseError = {
                type: 'EMPTY_SHEET',
                message: '엑셀 파일의 모든 시트가 비어있습니다.',
                details: { scannedSheets: workbook.SheetNames },
              };
              reject(new Error(formatParseError(error)));
              return;
            }

            // 첫 행의 헤더 확인
            const firstRowHeaders = (firstSheetData[0] || []).map((h: any) => normalizeHeader(h)).filter(Boolean);

            const error: ParseError = {
              type: 'HEADER_NOT_FOUND',
              message: '필수 헤더(사번)를 찾을 수 없습니다.',
              details: {
                foundHeaders: firstRowHeaders.slice(0, 10), // 최대 10개만 표시
                missingHeaders: REQUIRED_HEADERS,
                scannedSheets: workbook.SheetNames,
              },
            };
            reject(new Error(formatParseError(error)));
            return;
          }

          const worksheet = workbook.Sheets[bestSheet.sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

          // 헤더 행 (정규화 적용)
          const rawHeaders = jsonData[bestSheet.headerRowIndex] as any[];
          const headers = rawHeaders.map((h: any) => normalizeHeader(h));

          const rows: any[] = [];

          // 데이터 행 파싱 (헤더 행 다음부터)
          for (let i = bestSheet.headerRowIndex + 1; i < jsonData.length; i++) {
            const rowData = jsonData[i];
            if (!rowData || rowData.length === 0) continue;

            // 빈 행 스킵 (모든 셀이 비어있으면)
            const hasAnyValue = rowData.some((cell: any) =>
              cell !== undefined && cell !== null && cell !== ''
            );
            if (!hasAnyValue) continue;

            const row: any = {
              monthlyUsage: {},
              personalHealingQuarters: {},
            };

            for (let j = 0; j < headers.length; j++) {
              const header = headers[j];
              const value = rowData[j];

              const mappedField = LEAVE_EXCEL_COLUMN_MAPPING[header];
              if (mappedField) {
                if (mappedField.startsWith('month_')) {
                  const month = parseInt(mappedField.split('_')[1], 10);
                  if (value !== undefined && value !== null && value !== '') {
                    const parsed = parseFloat(value);
                    if (!isNaN(parsed)) {
                      row.monthlyUsage[month] = parsed;
                    }
                  }
                } else if (mappedField.startsWith('personal_')) {
                  // V45.1: 퍼힐 값 파싱 - "사용/미사용" 텍스트 지원
                  const quarter = parseInt(mappedField.split('_')[1], 10);
                  if (value !== undefined && value !== null && value !== '') {
                    const parsedValue = parsePersonalHealingValue(value);
                    if (parsedValue !== null) {
                      row.personalHealingQuarters[quarter] = parsedValue;
                    }
                  }
                } else {
                  row[mappedField] = value;
                }
              }
            }

            // 사번이 있는 행만 추가 (숫자도 문자열로 변환)
            if (row.employeeNumber !== undefined && row.employeeNumber !== null && row.employeeNumber !== '') {
              row.employeeNumber = String(row.employeeNumber).trim();
              rows.push(row);
            }
          }

          if (rows.length === 0) {
            const error: ParseError = {
              type: 'NO_DATA_ROWS',
              message: '헤더는 인식되었으나 데이터 행이 없습니다.',
              details: {
                foundHeaders: headers.filter(Boolean),
              },
            };
            reject(new Error(formatParseError(error)));
            return;
          }

          resolve(rows);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('파일을 읽는 중 오류가 발생했습니다.'));
      reader.readAsBinaryString(file);
    });
  };

  /**
   * V199: ParseError를 사용자 친화적 메시지로 변환
   */
  function formatParseError(error: ParseError): string {
    switch (error.type) {
      case 'EMPTY_SHEET':
        return `시트가 비어있습니다.\n검색한 시트: ${error.details?.scannedSheets?.join(', ') || '없음'}`;

      case 'HEADER_NOT_FOUND':
        const found = error.details?.foundHeaders?.length
          ? `발견된 헤더: ${error.details.foundHeaders.join(', ')}`
          : '헤더를 찾을 수 없습니다';
        const missing = error.details?.missingHeaders?.join(', ') || '';
        return `헤더가 일치하지 않습니다.\n${found}\n필수 컬럼: ${missing}`;

      case 'MISSING_REQUIRED':
        return `필수 컬럼이 누락되었습니다: ${error.details?.missingHeaders?.join(', ') || ''}`;

      case 'NO_DATA_ROWS':
        return `데이터 행이 없습니다.\n인식된 헤더: ${error.details?.foundHeaders?.join(', ') || ''}`;

      default:
        return error.message;
    }
  }

  // V199: 임포트 핸들러 (상세 에러 메시지)
  const handleImport = async (file: File) => {
    setIsImporting(true);
    setImportError(null);
    setImportResult(null);

    try {
      // 1. 엑셀 파싱 (V199: parseExcelFile 내부에서 상세 에러 처리)
      const rows = await parseExcelFile(file);

      // V199: 이 시점에서 rows.length === 0이면 parseExcelFile에서 이미 reject됨
      // 하지만 안전을 위해 이중 체크
      if (rows.length === 0) {
        throw new Error('임포트할 데이터가 없습니다. 엑셀 파일의 형식을 확인해주세요.');
      }

      // 2. CF 호출
      const functions = getFunctions();
      const importLeaveBalancesFn = httpsCallable(functions, 'importLeaveBalances');

      const result = await importLeaveBalancesFn({
        rows,
        year: targetYear,
        includePreviousYear,
      });

      const response = result.data as ImportResult;
      setImportResult(response);

      // 3. 성공 시 목록 새로고침
      if (response.successCount > 0) {
        refetch();
      }
    } catch (error: any) {
      // V199.1: 상세 에러 메시지 추출
      console.error('[V199.1] 임포트 실패:', error);

      let errorMessage = '임포트 중 오류가 발생했습니다.';

      if (error.code) {
        // Firebase HttpsError 구조
        const code = error.code;
        const message = error.message;
        const details = error.details;

        console.error('[V199.1] 에러 상세:', { code, message, details });

        switch (code) {
          case 'unauthenticated':
            errorMessage = '로그인이 필요합니다. 다시 로그인해주세요.';
            break;
          case 'permission-denied':
            errorMessage = '인사팀 또는 관리자만 휴가현황을 임포트할 수 있습니다.';
            break;
          case 'invalid-argument':
            errorMessage = message || '입력 데이터가 올바르지 않습니다.';
            break;
          case 'internal':
            // V199.1: internal 에러도 상세 메시지 표시
            errorMessage = message || '서버 내부 오류가 발생했습니다.';
            if (details?.originalError) {
              errorMessage += `\n상세: ${details.originalError}`;
            }
            break;
          default:
            errorMessage = message || `오류가 발생했습니다. (${code})`;
        }
      } else if (error.message) {
        // 일반 Error
        errorMessage = error.message;
      }

      setImportError(errorMessage);
    } finally {
      setIsImporting(false);
    }
  };

  // V197: 파일 선택 핸들러
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImport(file);
    }
    // 같은 파일 재선택 허용
    e.target.value = '';
  };

  // V197: 모달 닫기
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportResult(null);
    setImportError(null);
  };

  /**
   * V36.2: 전 직원 leaveBalances 일괄 Sync (커서 기반 반복 호출)
   *
   * - nextCursor가 null이 될 때까지 반복 호출
   * - 진행 상황 실시간 표시
   * - 중단 버튼으로 루프 중단 가능
   */
  const handleSyncAllLeaveBalances = useCallback(async () => {
    if (syncProgress.status === 'running') return;

    // V36.2.1 Hotfix: 사용자 정보 로딩 중이면 실행 차단
    if (userLoading) {
      alert('사용자 정보를 확인 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    // V36.2.1 Hotfix: 회사 선택 가능 여부 엄격 확인
    if (availableCompanyCodes.length === 0) {
      alert('회사 코드를 판정할 수 없습니다. 관리자에게 문의하세요.');
      return;
    }

    // V36.2.1 Hotfix: targetCompanyCode 엄격 결정
    // - 글로벌 관리자: 선택한 값이 availableCompanyCodes에 포함되어야 함
    // - 그 외: userCompanyCode만 사용 (폴백 'KR' 제거)
    let targetCompanyCode: 'KR' | 'CN';
    if (isGlobalAdmin) {
      if (!availableCompanyCodes.includes(selectedCompanyCode)) {
        alert('선택한 회사 코드에 대한 권한이 없습니다.');
        return;
      }
      targetCompanyCode = selectedCompanyCode;
    } else {
      if (!userCompanyCode) {
        alert('사용자의 회사 코드가 설정되지 않았습니다. 관리자에게 문의하세요.');
        return;
      }
      targetCompanyCode = userCompanyCode;
    }

    const confirmed = window.confirm(
      `[${targetCompanyCode}] 직원의 휴가 잔액을 일괄 동기화합니다.\n\n` +
      '이 작업은 leaveEntitlements(원장)를 기준으로 leaveBalances를 갱신합니다.\n' +
      '직원 수에 따라 수 분이 소요될 수 있습니다.\n\n' +
      '계속하시겠습니까?'
    );

    if (!confirmed) return;

    // AbortController 생성
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // 초기 상태 설정
    setSyncProgress({
      status: 'running',
      companyCode: targetCompanyCode,
      processed: 0,
      synced: 0,
      errors: 0,
      message: '동기화 시작...',
    });

    const functions = getFunctions();
    const syncFn = httpsCallable<SyncAllLeaveBalancesInput, SyncAllLeaveBalancesResponse>(
      functions,
      'syncAllLeaveBalances',
      { timeout: 540000 } // 9분 타임아웃
    );

    let cursor: string | null = null;
    let totalProcessed = 0;
    let totalSynced = 0;
    let totalErrors = 0;

    try {
      // V36.2: nextCursor가 null이 될 때까지 반복
      while (true) {
        // V36.2.1 Hotfix: 중단 확인 (요청 전)
        // 주의: AbortController는 httpsCallable의 네트워크 요청 취소를 지원하지 않음
        // 따라서 "다음 호출부터 중단"만 가능하며, 현재 진행 중인 요청은 완료됨
        if (signal.aborted) {
          setSyncProgress(prev => ({
            ...prev,
            status: 'aborted',
            message: `중단됨: ${totalProcessed}명 처리 완료 (성공 ${totalSynced}, 실패 ${totalErrors})`,
          }));
          return;
        }

        const response = await syncFn({
          companyCode: targetCompanyCode,
          cursor,
          limit: 100,
        });

        // V36.2.1 Hotfix: 요청 완료 후 중단 확인 (현재 요청 결과는 반영)
        const data: SyncAllLeaveBalancesResponse = response.data;

        totalProcessed += data.processedCount;
        totalSynced += data.syncedCount;
        totalErrors += data.errorCount;

        // 진행 상황 업데이트
        setSyncProgress(prev => ({
          ...prev,
          processed: totalProcessed,
          synced: totalSynced,
          errors: totalErrors,
          message: data.nextCursor
            ? `처리 중: ${totalProcessed}명 완료...`
            : `동기화 완료`,
        }));

        // 완료 조건: nextCursor가 null
        if (data.nextCursor === null) {
          break;
        }

        // V36.2.1 Hotfix: 응답 후 중단 확인 (현재 배치 결과는 이미 반영됨)
        if (signal.aborted) {
          setSyncProgress(prev => ({
            ...prev,
            status: 'aborted',
            message: `중단됨: ${totalProcessed}명 처리 완료 (성공 ${totalSynced}, 실패 ${totalErrors})`,
          }));
          return;
        }

        cursor = data.nextCursor;
      }

      // 완료 상태
      setSyncProgress(prev => ({
        ...prev,
        status: 'completed',
        message: totalErrors === 0
          ? `동기화 완료: ${totalSynced}명 성공`
          : `동기화 완료: ${totalSynced}명 성공, ${totalErrors}명 실패`,
      }));

      // 성공 시 목록 새로고침
      if (totalSynced > 0) {
        refetch();
      }

      // 결과 알림
      if (totalErrors === 0) {
        alert(`[${targetCompanyCode}] 일괄 동기화 완료!\n\n총 ${totalProcessed}명 중 ${totalSynced}명 동기화 성공`);
      } else {
        alert(
          `[${targetCompanyCode}] 일괄 동기화 완료 (일부 실패)\n\n` +
          `처리: ${totalProcessed}명\n` +
          `성공: ${totalSynced}명\n` +
          `실패: ${totalErrors}명\n\n` +
          `상세 로그는 Cloud Logging에서 확인하세요.`
        );
      }

    } catch (error: any) {
      console.error('[V36.2] syncAllLeaveBalances 실패:', error);

      let errorMessage = '일괄 동기화 중 오류가 발생했습니다.';

      if (error.code === 'permission-denied') {
        errorMessage = '인사팀 또는 관리자만 일괄 동기화를 실행할 수 있습니다.';
      } else if (error.code === 'unauthenticated') {
        errorMessage = '로그인이 필요합니다.';
      } else if (error.code === 'deadline-exceeded') {
        errorMessage = '서버 응답 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSyncProgress(prev => ({
        ...prev,
        status: 'failed',
        message: errorMessage,
      }));

      alert(errorMessage);
    }
  }, [syncProgress.status, selectedCompanyCode, isGlobalAdmin, userCompanyCode, availableCompanyCodes, userLoading, refetch]);

  /**
   * V36.2.1 Hotfix: 동기화 중단
   *
   * 주의: Firebase httpsCallable은 AbortController를 지원하지 않음
   * - 이미 진행 중인 네트워크 요청은 취소되지 않음
   * - 현재 요청이 완료된 후 다음 루프에서 중단됨
   */
  const handleAbortSync = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      // 즉시 UI에 중단 요청 상태 표시
      setSyncProgress(prev => ({
        ...prev,
        message: '중단 요청됨 (현재 배치 완료 후 중단)',
      }));
    }
  }, []);

  /**
   * V36.2: 진행 상태 초기화
   */
  const handleResetSync = useCallback(() => {
    setSyncProgress({
      status: 'idle',
      companyCode: '',
      processed: 0,
      synced: 0,
      errors: 0,
      message: '',
    });
    abortControllerRef.current = null;
  }, []);

  // V200.1: employees 기준으로 결합 (leaveBalances가 없어도 직원은 표시)
  // V36.2.3: employeeId 필드 우선, 없으면 doc.id로 폴백 (레거시 문서 호환)
  const combinedData = employees.map((employee) => {
    const balance = balances.find((b) =>
      b.employeeId === employee.id || b.id === employee.id
    );
    return {
      employee,
      balance, // undefined일 수 있음
    };
  });

  // V189: 월별 사용량 조회를 위한 employeeIds 및 periods 준비
  const employeeIds = useMemo(() => {
    return combinedData
      .filter((item) => item.balance?.employeeId)
      .map((item) => item.balance!.employeeId);
  }, [combinedData]);

  const employeePeriods = useMemo(() => {
    const periods: Record<string, PeriodInfo> = {};
    for (const item of combinedData) {
      if (item.balance?.employeeId && item.balance.annual) {
        periods[item.balance.employeeId] = {
          validFrom: item.balance.annual.validFrom,
          validUntil: item.balance.annual.validUntil,
          periodStart: item.balance.annual.periodStart,
          periodEnd: item.balance.annual.periodEnd,
        };
      }
    }
    return periods;
  }, [combinedData]);

  // V189: 월별 사용량 일괄 조회 (leaveHistories SSoT 기반)
  // V311: monthlyRefreshKey로 저장 후 강제 재조회
  const { usageMap: monthlyUsageMap, isLoading: usageLoading } = useLeaveMonthlyUsageBatch(
    employeeIds,
    employeePeriods,
    'annual',
    monthlyRefreshKey
  );

  // V200.1: employees 기준 필터링
  const filteredData = combinedData.filter((item) => {
    const displayName = item.employee?.nameKo || item.balance?.employeeName || '';
    const orgUnitName = item.employee?.orgUnitName || '';

    const matchesSearch =
      !searchText ||
      displayName.toLowerCase().includes(searchText.toLowerCase()) ||
      orgUnitName.toLowerCase().includes(searchText.toLowerCase()) ||
      (item.employee?.employeeNumber || '').toLowerCase().includes(searchText.toLowerCase());

    const matchesStatus =
      filterStatus === 'all' || item.employee?.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  /**
   * V70.4: 엑셀 다운로드
   *
   * 범위 정책
   * - 대상 직원: `combinedData` **전체** (검색어/상태 필터 미적용).
   *   요구사항이 "권한 범위의 휴가현황 전체 직원"이고, 기존 엑셀 다운로드
   *   (HrEmployeesPage / HrEmploymentStatusPage) 도 화면 필터와 무관하게 전체를 내보낸다.
   *   이 페이지에는 페이지네이션이 없어 화면도 이미 전체를 렌더한다.
   * - 회사 범위: 별도 판정 없음. 데이터 출처인 `getEmployees` callable 이
   *   `getValidatedCompanyFilter`(BE SSoT)로 회사 스코프를 이미 적용한다.
   *   → KR 관리자=KR만 / CN 관리자=CN만 / 통합 Admin=KR+CN 이 자동 결정된다.
   * - 값: `deriveLeaveOverviewRow` 재사용 → 화면 테이블과 동일 값.
   *
   * READ-ONLY: Firestore write 없음 (이미 조회된 데이터만 사용).
   */
  const handleExcelDownload = () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      if (combinedData.length === 0) {
        alert('다운로드할 데이터가 없습니다.');
        return;
      }

      const COMPANY_LABELS: Record<string, string> = { KR: 'KR', CN: 'CN' };
      const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

      const exportData = combinedData.map((item) => {
        const row = deriveLeaveOverviewRow(item, monthlyUsageMap);
        const record: Record<string, string | number> = {
          // V70.4: 통합 Admin 엑셀에서 KR/CN 구분이 가능하도록 회사 컬럼 포함
          '회사': COMPANY_LABELS[row.companyCode] || row.companyCode || '-',
          '사번': row.employeeNumber,
          '이름': row.nameKo,
          '조직': row.orgUnitName,
          '발생': row.granted,
          '사용': row.used,
          '선사용': row.preUsed,
          '잔여': row.remaining,
          '적용시작': row.validFrom,
          '적용종료': row.validUntil,
        };
        // 월별 사용량 — 화면과 동일하게 값 없으면 '-'
        for (const m of MONTHS) {
          const v = row.monthlyUsage[m];
          record[`${m}월`] = v !== undefined ? v : '-';
        }
        record['퍼힐1Q'] = row.q1Used ? '사용' : '-';
        record['퍼힐2Q'] = row.q2Used ? '사용' : '-';
        record['퍼힐3Q'] = row.q3Used ? '사용' : '-';
        record['퍼힐4Q'] = row.q4Used ? '사용' : '-';
        return record;
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const headers = Object.keys(exportData[0]);
      worksheet['!cols'] = headers.map((h) => ({
        wch: /^\d+월$|^퍼힐/.test(h) ? 7 : Math.max(h.length * 2, 10),
      }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '휴가현황');

      // 파일명: 기존 프로젝트 규칙(_YYYYMMDD)을 따름
      // 범위 라벨은 이 페이지의 기존 권한 판정(isGlobalAdmin/userCompanyCode)을 재사용
      const presentCompanies = Array.from(
        new Set(exportData.map((r) => String(r['회사'])).filter((c) => c === 'KR' || c === 'CN'))
      );
      const scopeLabel = isGlobalAdmin
        ? '전체'
        : (userCompanyCode || (presentCompanies.length === 1 ? presentCompanies[0] : '전체'));
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      XLSX.writeFile(workbook, `휴가현황_${scopeLabel}_${dateStr}.xlsx`);
    } catch (err: any) {
      console.error('[V70.4] 휴가현황 엑셀 다운로드 실패:', err);
      alert(err?.message || '엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setIsExporting(false);
    }
  };

  // V200.1: 통계 계산 (balance 기준)
  const stats = {
    totalEmployees: combinedData.length,
    totalAnnualRemaining: combinedData.reduce(
      (sum, item) => sum + (item.balance?.annual?.remaining || 0),
      0
    ),
    totalAnnualUsed: combinedData.reduce(
      (sum, item) => sum + (item.balance?.annual?.used || 0),
      0
    ),
    totalCompensatoryGranted: combinedData.reduce(
      (sum, item) => sum + (item.balance?.compensatory?.granted || 0),
      0
    ),
    totalCompensatoryUsed: combinedData.reduce(
      (sum, item) => sum + (item.balance?.compensatory?.used || 0),
      0
    ),
    totalCompensatoryRemaining: combinedData.reduce(
      (sum, item) => sum + (item.balance?.compensatory?.remaining || 0),
      0
    ),
    // V64 최적화: pendingRequests 제거 (UI 미사용, 전체 leaveRequests 스캔 원인)
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', minHeight: 0, overflow: 'hidden' }}>
      {/* V67: 페이지 제목 + 휴직 현황 보기 버튼 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          marginBottom: '8px',
          flexShrink: 0,
        }}
      >
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-gray-800)', margin: 0 }}>
          휴가 현황
        </h1>
        {/* V67: 휴직 현황 보기 — leaveOfAbsences.isCurrent SSoT 기준 */}
        <button
          type="button"
          onClick={() => navigate('/hr/leave-of-absences')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            background: 'white',
            border: '1px solid var(--color-blue-500)',
            color: 'var(--color-blue-600)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          <UserMinus size={16} /> 휴직 현황 보기
        </button>
      </div>
      {/* V36.2.5: 대상 범위 안내 */}
      <p
        style={{
          fontSize: '13px',
          color: 'var(--color-gray-500)',
          marginBottom: '24px',
          flexShrink: 0,
        }}
      >
        재직/휴직자 기준 (퇴사자 제외)
      </p>

      {/* 전체 통계 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <div
            style={{ fontSize: '14px', color: 'var(--color-gray-600)', marginBottom: '8px' }}
          >
            전체 직원
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--color-blue-600)' }}>
            {stats.totalEmployees}명
          </div>
        </div>
      </div>

      {/* 필터 및 검색 */}
      <div
        style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '16px',
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Filter size={16} style={{ color: 'var(--color-gray-600)' }} />
          <input
            type="text"
            placeholder="이름 또는 조직 검색..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
              width: '100%',
              maxWidth: '300px',
              minWidth: '150px',
            }}
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid var(--color-gray-300)',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          <option value="all">전체 상태</option>
          <option value="active">재직</option>
          <option value="leave">휴직</option>
          <option value="inactive">퇴사</option>
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center' }}>
          {/* V60: role_exec는 동기화/임포트 버튼 숨김 */}
          {canManageHr && (<>
          {/* V36.2: 회사 선택 드롭다운 (전역 관리자만) */}
          {isGlobalAdmin && availableCompanyCodes.length > 1 && syncProgress.status === 'idle' && (
            <select
              value={selectedCompanyCode}
              onChange={(e) => setSelectedCompanyCode(e.target.value as 'KR' | 'CN')}
              style={{
                padding: '8px 12px',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '6px',
                fontSize: '14px',
                background: 'white',
              }}
              title="동기화할 회사 선택"
            >
              <option value="KR">KR</option>
              <option value="CN">CN</option>
            </select>
          )}

          {/* V36.2: 진행 상황 표시 */}
          {syncProgress.status !== 'idle' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                background: syncProgress.status === 'running'
                  ? 'var(--color-blue-50)'
                  : syncProgress.status === 'completed'
                  ? 'var(--color-green-50)'
                  : syncProgress.status === 'aborted'
                  ? 'var(--color-orange-50)'
                  : 'var(--color-red-50)',
                borderRadius: '6px',
                fontSize: '13px',
                color: syncProgress.status === 'running'
                  ? 'var(--color-blue-700)'
                  : syncProgress.status === 'completed'
                  ? 'var(--color-green-700)'
                  : syncProgress.status === 'aborted'
                  ? 'var(--color-orange-700)'
                  : 'var(--color-red-700)',
              }}
            >
              <span style={{ fontWeight: 500 }}>[{syncProgress.companyCode}]</span>
              <span>처리: {syncProgress.processed}명</span>
              <span>|</span>
              <span style={{ color: 'var(--color-green-600)' }}>성공: {syncProgress.synced}</span>
              {syncProgress.errors > 0 && (
                <>
                  <span>|</span>
                  <span style={{ color: 'var(--color-red-600)' }}>실패: {syncProgress.errors}</span>
                </>
              )}
            </div>
          )}

          {/* V36.2: 동기화 시작/중단 버튼 */}
          {syncProgress.status === 'running' ? (
            <button
              onClick={handleAbortSync}
              style={{
                padding: '8px 16px',
                background: 'var(--color-orange-600)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              title="동기화를 중단합니다"
            >
              <Square size={16} />
              중단
            </button>
          ) : syncProgress.status !== 'idle' ? (
            <button
              onClick={handleResetSync}
              style={{
                padding: '8px 16px',
                background: 'var(--color-gray-500)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              title="상태를 초기화합니다"
            >
              <X size={16} />
              닫기
            </button>
          ) : (
            <button
              onClick={handleSyncAllLeaveBalances}
              disabled={syncButtonDisabled}
              style={{
                padding: '8px 16px',
                background: syncButtonDisabled ? 'var(--color-gray-400)' : 'var(--color-purple-600)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: syncButtonDisabled ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              title={syncButtonDisabled ? syncButtonDisabledReason : '전 직원의 leaveBalances를 leaveEntitlements 기준으로 일괄 동기화합니다'}
            >
              <RefreshCw size={16} />
              {userLoading ? '로딩 중...' : '동기화'}
            </button>
          )}

          {/* V197: 엑셀 임포트 버튼 */}
          <button
            onClick={() => setShowImportModal(true)}
            style={{
              padding: '8px 16px',
              background: 'var(--color-blue-600)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Upload size={16} />
            엑셀 임포트
          </button>
          </>)}

          {/* V70.4: 실제 엑셀 다운로드 (권한 범위 전체 직원, 화면과 동일 값) */}
          <button
            onClick={handleExcelDownload}
            disabled={isExporting || employeesLoading || balancesLoading}
            title={isExporting ? '다운로드 중...' : '휴가현황 전체 엑셀 다운로드'}
            style={{
              padding: '8px 16px',
              background: (isExporting || employeesLoading || balancesLoading)
                ? 'var(--color-gray-400)'
                : 'var(--color-green-600)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (isExporting || employeesLoading || balancesLoading) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Download size={16} />
            {isExporting ? '다운로드 중...' : '엑셀 다운로드'}
          </button>
        </div>
      </div>

      {/* 직원 휴가 현황 테이블 */}
      <div
        style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <h2
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
          }}
        >
          <Users size={20} />
          직원별 휴가 현황
        </h2>

        {/* V200.1: employees + balances 모두 로딩 완료 후 표시 */}
        {(balancesLoading || employeesLoading) ? (
          <div>로딩 중...</div>
        ) : filteredData.length > 0 ? (
          <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
            <table style={{ borderCollapse: 'collapse', border: '1px solid var(--color-gray-100)', width: '100%', minWidth: '1400px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                  {/* V43: 헤더 순서 - 수정/사번/이름/조직/발생/사용/선사용/잔여/적용시작/적용종료/1~12월/퍼힐1~4분기 */}
                  {/* V60: role_exec는 수정 컬럼 숨김 */}
                  {canManageHr && <th style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>수정</th>}
                  <th style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>사번</th>
                  <th style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>이름</th>
                  <th style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>조직</th>
                  <th style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>발생</th>
                  <th style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>사용</th>
                  <th style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>선사용</th>
                  <th style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>잔여</th>
                  <th style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>적용시작</th>
                  <th style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>적용종료</th>
                  {/* 월별 사용량 1~12월 */}
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                    <th key={`month-${m}`} style={{ padding: '6px 1px', textAlign: 'center', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>{m}월</th>
                  ))}
                  {/* 퍼힐 1~4분기 */}
                  <th style={{ padding: '6px 1px', textAlign: 'center', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>퍼힐1Q</th>
                  <th style={{ padding: '6px 1px', textAlign: 'center', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>퍼힐2Q</th>
                  <th style={{ padding: '6px 1px', textAlign: 'center', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>퍼힐3Q</th>
                  <th style={{ padding: '6px 1px', textAlign: 'center', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>퍼힐4Q</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item) => {
                  // V200.1: balance가 없으면 기본값 사용
                  const balance = item.balance;
                  // V189: 월별 사용량 (leaveHistories SSoT 기반)
                  // V65-FIX(2026-04-22): 우선순위 반전 — balance(DB)를 우선, histories fallback
                  // 이유: useLeaveMonthlyUsageBatch는 source='import' 레코드를 월별 분해하지 않고
                  // importTotal로만 합산하므로, import에 포함된 월별 분포가 누락되어 표시됨.
                  // BE(leaveEntitlementEngine.ts)가 baseline(importedMonthlyUsage) + delta로
                  // 재계산하여 leaveBalances.annual.monthlyUsage에 정답을 저장하므로 DB를 우선한다.
                  // V70.4: 표시값 산출을 deriveLeaveOverviewRow 로 일원화
                  //   (엑셀 다운로드가 동일 함수를 사용 → 화면 값 = 엑셀 값 보장)
                  const row = deriveLeaveOverviewRow(item, monthlyUsageMap);
                  const monthlyUsage: Record<number | string, number> = row.monthlyUsage;
                  // 퍼힐 분기별 boolean (balance 없으면 모두 false → 미사용)
                  const q1 = row.q1Used;
                  const q2 = row.q2Used;
                  const q3 = row.q3Used;
                  const q4 = row.q4Used;

                  return (
                    <tr key={item.employee?.id || `unknown-${Math.random()}`} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                      {/* V43: 수정 버튼 (V60: role_exec 숨김) */}
                      {canManageHr && (
                        <td style={{ padding: '4px', textAlign: 'center', minWidth: '50px', borderRight: '1px solid var(--color-gray-100)' }}>
                          <button
                            onClick={() => {
                              if (item.employee?.id) {
                                setEditTargetEmployee({
                                  employeeId: item.employee.id,
                                  employeeName: item.employee.nameKo || balance?.employeeName || '-',
                                  employeeNumber: item.employee.employeeNumber,
                                  balance: balance || null,
                                });
                                setShowEditModal(true);
                              }
                            }}
                            disabled={!item.employee?.id}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid var(--color-gray-300)',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              cursor: item.employee?.id ? 'pointer' : 'not-allowed',
                              fontSize: '12px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                            title="휴가현황 수정"
                          >
                            <Edit2 size={12} />
                          </button>
                        </td>
                      )}
                      {/* 사번 */}
                      <td style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)' }}>
                        {row.employeeNumber}
                      </td>
                      {/* 이름 (V82: 클릭 시 상세 사용 내역 모달) */}
                      <td style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)' }}>
                        {item.employee?.id ? (
                          <button
                            type="button"
                            onClick={() => {
                              setUsageDetailTarget({
                                employeeId: item.employee!.id,
                                employeeName: item.employee?.nameKo || balance?.employeeName || '-',
                                employeeNumber: item.employee?.employeeNumber,
                              });
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              padding: 0,
                              margin: 0,
                              fontSize: '12px',
                              color: 'inherit',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                            }}
                            title="상세 휴가 사용 내역 보기"
                          >
                            {row.nameKo}
                          </button>
                        ) : (
                          <>{row.nameKo}</>
                        )}
                      </td>
                      {/* 조직 */}
                      <td style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px', borderRight: '1px solid var(--color-gray-100)' }} title={item.employee?.orgUnitName ?? ''}>
                        {row.orgUnitName}
                      </td>
                      {/* 발생 */}
                      <td style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', borderRight: '1px solid var(--color-gray-100)' }}>
                        {row.granted}
                      </td>
                      {/* V40: 사용 (annual.used) */}
                      <td style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', borderRight: '1px solid var(--color-gray-100)' }}>
                        {row.used}
                      </td>
                      {/* V39.x: 선사용 연차 (preUsed만 사용) */}
                      <td style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', borderRight: '1px solid var(--color-gray-100)', color: ((balance?.annual?.preUsed ?? 0) > 0) ? 'var(--color-orange-600)' : undefined }}>
                        {row.preUsed}
                      </td>
                      {/* V200.7: 잔여 */}
                      <td style={{ padding: '6px 2px', textAlign: 'center', fontSize: '12px', borderRight: '1px solid var(--color-gray-100)', color: ((balance?.annual?.remaining ?? 0) < 0) ? 'var(--color-red-600)' : undefined, fontWeight: ((balance?.annual?.remaining ?? 0) < 0) ? 600 : undefined }}>
                        {row.remaining}
                      </td>
                      {/* 적용시작 */}
                      <td style={{ padding: '6px 2px', textAlign: 'center', fontSize: '11px', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)' }}>
                        {row.validFrom}
                      </td>
                      {/* 적용종료 */}
                      <td style={{ padding: '6px 2px', textAlign: 'center', fontSize: '11px', whiteSpace: 'nowrap', borderRight: '1px solid var(--color-gray-100)' }}>
                        {row.validUntil}
                      </td>
                      {/* 1~12월 */}
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
                        const val = monthlyUsage[m];
                        return (
                          <td key={`m-${m}`} style={{ padding: '6px 1px', textAlign: 'center', fontSize: '12px', borderRight: '1px solid var(--color-gray-100)' }}>
                            {val !== undefined ? val : '-'}
                          </td>
                        );
                      })}
                      {/* 퍼힐 1~4분기 (사용/미사용) */}
                      <td style={{ padding: '6px 1px', textAlign: 'center', fontSize: '11px', borderRight: '1px solid var(--color-gray-100)', color: q1 ? 'var(--color-green-600)' : 'var(--color-gray-400)' }}>
                        {q1 ? '사용' : '-'}
                      </td>
                      <td style={{ padding: '6px 1px', textAlign: 'center', fontSize: '11px', borderRight: '1px solid var(--color-gray-100)', color: q2 ? 'var(--color-green-600)' : 'var(--color-gray-400)' }}>
                        {q2 ? '사용' : '-'}
                      </td>
                      <td style={{ padding: '6px 1px', textAlign: 'center', fontSize: '11px', borderRight: '1px solid var(--color-gray-100)', color: q3 ? 'var(--color-green-600)' : 'var(--color-gray-400)' }}>
                        {q3 ? '사용' : '-'}
                      </td>
                      <td style={{ padding: '6px 1px', textAlign: 'center', fontSize: '11px', color: q4 ? 'var(--color-green-600)' : 'var(--color-gray-400)' }}>
                        {q4 ? '사용' : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-gray-500)' }}>
            검색 결과가 없습니다.
          </div>
        )}
      </div>

      {/* V197: 엑셀 임포트 모달 */}
      {showImportModal && (
        <ModalOverlay
          onClose={closeImportModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
          >
            {/* 모달 헤더 */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                휴가현황 엑셀 임포트
              </h2>
              <button
                onClick={closeImportModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* 설정 영역 */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>
                    대상 연도
                  </label>
                  <select
                    value={targetYear}
                    onChange={(e) => setTargetYear(parseInt(e.target.value, 10))}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid var(--color-gray-300)',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                    disabled={isImporting}
                  >
                    {[2024, 2025, 2026].map((year) => (
                      <option key={year} value={year}>
                        {year}년
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="includePreviousYear"
                    checked={includePreviousYear}
                    onChange={(e) => setIncludePreviousYear(e.target.checked)}
                    disabled={isImporting}
                  />
                  <label htmlFor="includePreviousYear" style={{ fontSize: '14px' }}>
                    선사용 연차 포함
                  </label>
                </div>
              </div>

              <div
                style={{
                  padding: '12px',
                  background: 'var(--color-blue-50)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: 'var(--color-blue-700)',
                }}
              >
                <strong>엑셀 컬럼 형식:</strong>
                <br />
                사번, 이름, 조직, 발생일, 선사용연차, 적용시작일, 적용종료일, 1월~12월, 사용일,
                퍼스널1~4분기, 잔여일
              </div>
            </div>

            {/* 파일 업로드 영역 */}
            <div
              style={{
                border: '2px dashed var(--color-gray-300)',
                borderRadius: '8px',
                padding: '40px 20px',
                textAlign: 'center',
                marginBottom: '20px',
                background: isImporting ? 'var(--color-gray-50)' : 'white',
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                disabled={isImporting}
              />

              {isImporting ? (
                <div>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      border: '3px solid var(--color-gray-300)',
                      borderTop: '3px solid var(--color-blue-600)',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 12px',
                    }}
                  />
                  <p style={{ color: 'var(--color-gray-600)', margin: 0 }}>
                    임포트 중...
                  </p>
                </div>
              ) : (
                <>
                  <Upload size={40} style={{ color: 'var(--color-gray-400)', marginBottom: '12px' }} />
                  <p style={{ color: 'var(--color-gray-600)', marginBottom: '12px' }}>
                    엑셀 파일을 드래그하거나 클릭하여 선택하세요
                  </p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      padding: '10px 20px',
                      background: 'var(--color-blue-600)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    파일 선택
                  </button>
                </>
              )}
            </div>

            {/* 에러 메시지 */}
            {importError && (
              <div
                style={{
                  padding: '12px',
                  background: 'var(--color-red-50)',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <AlertCircle size={18} style={{ color: 'var(--color-red-600)' }} />
                <span style={{ color: 'var(--color-red-700)', fontSize: '14px' }}>
                  {importError}
                </span>
              </div>
            )}

            {/* 결과 표시 */}
            {importResult && (
              <div>
                {/* V47.14: 요약 표시 (기간불일치 포함) */}
                <div
                  style={{
                    padding: '12px',
                    background: importResult.failureCount === 0 && (importResult.mismatchCount || 0) === 0
                      ? 'var(--color-green-50)'
                      : importResult.failureCount === 0
                        ? 'var(--color-blue-50)'
                        : 'var(--color-yellow-50)',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <CheckCircle
                    size={18}
                    style={{
                      color: importResult.failureCount === 0 && (importResult.mismatchCount || 0) === 0
                        ? 'var(--color-green-600)'
                        : importResult.failureCount === 0
                          ? 'var(--color-blue-600)'
                          : 'var(--color-yellow-600)',
                    }}
                  />
                  <span style={{ fontSize: '14px' }}>
                    총 {importResult.totalCount}건 중 성공 {importResult.successCount}건,
                    실패 {importResult.failureCount}건
                    {(importResult.mismatchCount || 0) > 0 && (
                      <span style={{ color: 'var(--color-blue-600)', fontWeight: 500 }}>
                        , 기간불일치 {importResult.mismatchCount}건
                      </span>
                    )}
                  </span>
                </div>

                {/* 실패 목록 */}
                {importResult.failureCount > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontWeight: 500, marginBottom: '8px', color: 'var(--color-red-600)' }}>
                      ⚠️ 실패 목록 ({importResult.failureCount}건)
                    </div>
                    <div
                      style={{
                        maxHeight: '200px',
                        overflow: 'auto',
                        border: '1px solid var(--color-gray-200)',
                        borderRadius: '6px',
                      }}
                    >
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ background: 'var(--color-gray-50)' }}>
                            <th style={{ padding: '8px', textAlign: 'left' }}>행</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>사번</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>이름</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>사유</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importResult.results
                            .filter((r) => !r.success)
                            .map((r) => (
                              <tr key={r.rowIndex} style={{ borderTop: '1px solid var(--color-gray-200)' }}>
                                <td style={{ padding: '8px' }}>{r.rowIndex}</td>
                                <td style={{ padding: '8px' }}>{r.employeeNumber || '-'}</td>
                                <td style={{ padding: '8px' }}>{r.employeeName || '-'}</td>
                                <td style={{ padding: '8px', color: 'var(--color-red-600)' }}>
                                  {r.error}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* V47.14: 기간 불일치 목록 */}
                {importResult.mismatchPeriods && importResult.mismatchPeriods.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontWeight: 500, marginBottom: '8px', color: 'var(--color-blue-600)' }}>
                      ⚡ 기간 불일치 ({importResult.mismatchPeriods.length}건) - 엑셀 기간과 시스템 정책 기간이 다릅니다
                    </div>
                    <div
                      style={{
                        maxHeight: '200px',
                        overflow: 'auto',
                        border: '1px solid var(--color-blue-200)',
                        borderRadius: '6px',
                      }}
                    >
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr style={{ background: 'var(--color-blue-50)' }}>
                            <th style={{ padding: '6px', textAlign: 'left' }}>사번</th>
                            <th style={{ padding: '6px', textAlign: 'left' }}>이름</th>
                            <th style={{ padding: '6px', textAlign: 'left' }}>엑셀 기간</th>
                            <th style={{ padding: '6px', textAlign: 'left' }}>시스템 기간</th>
                            <th style={{ padding: '6px', textAlign: 'left' }}>사유</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importResult.mismatchPeriods.map((m, idx) => (
                            <tr key={`${m.employeeId}-${idx}`} style={{ borderTop: '1px solid var(--color-blue-100)' }}>
                              <td style={{ padding: '6px' }}>{m.employeeNumber || '-'}</td>
                              <td style={{ padding: '6px' }}>{m.employeeName || '-'}</td>
                              <td style={{ padding: '6px', fontSize: '11px' }}>
                                {m.excelValidFrom && m.excelValidUntil
                                  ? `${m.excelValidFrom} ~ ${m.excelValidUntil}`
                                  : <span style={{ color: 'var(--color-gray-400)' }}>미입력</span>
                                }
                              </td>
                              <td style={{ padding: '6px', fontSize: '11px' }}>
                                {m.systemValidFrom && m.systemValidUntil
                                  ? `${m.systemValidFrom} ~ ${m.systemValidUntil}`
                                  : <span style={{ color: 'var(--color-gray-400)' }}>계산불가</span>
                                }
                              </td>
                              <td style={{ padding: '6px', color: 'var(--color-blue-600)' }}>
                                {m.reason === 'MISMATCH' && '불일치'}
                                {m.reason === 'EXCEL_MISSING' && '엑셀 누락'}
                                {m.reason === 'SYSTEM_MISSING' && '시스템 계산실패'}
                                {m.reason === 'COMPUTE_ERROR' && '계산 오류'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* V64: 발생일수 정책 불일치 표시 */}
                {importResult.grantedMismatches && importResult.grantedMismatches.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontWeight: 500, marginBottom: '8px', color: 'var(--color-orange-600)' }}>
                      ⚠ 발생일수 자동 보정 ({importResult.grantedMismatches.length}건) - 입사 1년 미만 직원의 발생일수를 정책 기준으로 보정
                    </div>
                    <div
                      style={{
                        maxHeight: '200px',
                        overflow: 'auto',
                        border: '1px solid var(--color-orange-200)',
                        borderRadius: '6px',
                      }}
                    >
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr style={{ background: 'var(--color-orange-50)' }}>
                            <th style={{ padding: '6px', textAlign: 'left' }}>사번</th>
                            <th style={{ padding: '6px', textAlign: 'left' }}>이름</th>
                            <th style={{ padding: '6px', textAlign: 'right' }}>엑셀 발생</th>
                            <th style={{ padding: '6px', textAlign: 'right' }}>정책 발생</th>
                            <th style={{ padding: '6px', textAlign: 'left' }}>사유</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importResult.grantedMismatches.map((m: any, idx: number) => (
                            <tr key={`granted-${m.employeeId}-${idx}`} style={{ borderTop: '1px solid var(--color-orange-100)' }}>
                              <td style={{ padding: '6px' }}>{m.employeeNumber || '-'}</td>
                              <td style={{ padding: '6px' }}>{m.employeeName || '-'}</td>
                              <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600, color: 'var(--color-orange-600)' }}>{m.excelGranted}일</td>
                              <td style={{ padding: '6px', textAlign: 'right' }}>{m.policyGranted}일</td>
                              <td style={{ padding: '6px', color: 'var(--color-orange-600)', fontSize: '11px' }}>{m.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 모달 푸터 */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={closeImportModal}
                style={{
                  padding: '10px 20px',
                  background: 'var(--color-gray-200)',
                  color: 'var(--color-gray-700)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                닫기
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* V197: 스피너 애니메이션 */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* V82: 상세 휴가 사용 내역 모달 */}
      {usageDetailTarget && (
        <EmployeeLeaveUsageDetailModal
          isOpen={true}
          onClose={() => setUsageDetailTarget(null)}
          employeeId={usageDetailTarget.employeeId}
          employeeName={usageDetailTarget.employeeName}
          employeeNumber={usageDetailTarget.employeeNumber}
        />
      )}

      {/* V43: 휴가현황 수동 수정 모달 */}
      {editTargetEmployee && (
        <LeaveBalanceEditModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditTargetEmployee(null);
          }}
          employeeId={editTargetEmployee.employeeId}
          employeeName={editTargetEmployee.employeeName}
          employeeNumber={editTargetEmployee.employeeNumber}
          balance={editTargetEmployee.balance}
          onSaveSuccess={() => {
            // 저장 성공 후 목록 갱신
            refetch();
            // V311: 월별 사용량도 강제 재조회 (leaveHistories 재집계)
            setMonthlyRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}
